import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockPrManagementService } from '@/services/mock/MockPrManagementService';
import { PrFilterState, PrMetricsSummary, PrRecord } from '@/types/domain/pr-management';
import { PrKpiCards } from '@/components/pr/PrKpiCards';
import { PrTable } from '@/components/pr/PrTable';
import { PrConfirmationModal } from '@/components/pr/PrConfirmationModal';
import { Button } from '@/components/ui/Button';
import { Layers, Plus, RefreshCw, Search, ArrowUp } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

export const PrListPage: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<PrMetricsSummary | null>(null);
  const [records, setRecords] = useState<PrRecord[]>([]);
  const [visibleRecordsCount, setVisibleRecordsCount] = useState<number>(15);
  const [isLoading, setIsLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Cancellation modal state
  const [cancelPrId, setCancelPrId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const [filters, setFilters] = useState<PrFilterState>({
    search: '',
    status: 'ALL',
    department: 'ALL',
    plant: 'ALL',
    companyCode: 'ALL',
    costCenter: 'ALL',
    priority: 'ALL',
    page: 1,
    pageSize: 100, // Fetch broader pool for seamless lazy scrolling
  });

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [m, r] = await Promise.all([
        mockPrManagementService.getPrMetrics(),
        mockPrManagementService.getPrRecords(filters),
      ]);
      setMetrics(m);
      setRecords(r.items);
      setVisibleRecordsCount(15); // Reset lazy slice
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters]);

  // Handle Back to Top & Lazy Scroll Detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      setShowBackToTop(scrollY > 250);

      // Lazy load more records when user scrolls near the bottom (within 400px)
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollY + windowHeight >= docHeight - 400) {
        setVisibleRecordsCount((prev) => Math.min(prev + 10, records.length));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [records.length]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleConfirmCancel = async () => {
    if (!cancelPrId) return;
    try {
      setIsCancelling(true);
      await mockPrManagementService.cancelPr(cancelPrId, 'Cancelled by user via PR Control Center');
      setCancelPrId(null);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to cancel PR');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRetrySap = async (id: string) => {
    try {
      await mockPrManagementService.retrySapSync(id);
      await loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to retry SAP sync');
    }
  };

  const displayedRecords = records.slice(0, visibleRecordsCount);

  return (
    <div className="space-y-6 pb-20 font-poppins relative">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5">
            <Layers className="h-7 w-7 text-primary" /> Enterprise PR Control Center
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1">
            Real-time management, lifecycle validation, and SAP S/4HANA sync tracking for Purchase Requisitions.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            className="rounded-xl font-bold border-border text-foreground hover:bg-muted"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>

          <Button
            onClick={() => navigate(ROUTES.PR.NEW)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-2xs cursor-pointer"
          >
            <Plus className="h-4 w-4 mr-1.5" /> + Create PR
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {metrics && (
        <PrKpiCards
          metrics={metrics}
          onFilterClick={(stg) => setFilters((prev) => ({ ...prev, status: stg }))}
        />
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-2xl border border-border bg-card shadow-2xs">
        <div className="flex items-center gap-3 flex-1 min-w-[280px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-foreground-muted" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="Search PR #, Request #, SAP PR, Title, Cost Center..."
              className="w-full rounded-xl border border-border bg-background pl-9 pr-4 py-2 text-xs font-semibold text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <select
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            className="rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-hidden cursor-pointer"
          >
            <option value="ALL">All Statuses</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="APPROVED">Approved</option>
            <option value="SAP_CREATED">SAP Created</option>
            <option value="VALIDATION_FAILED">Validation Failed</option>
          </select>

          <select
            value={filters.department}
            onChange={(e) => setFilters((prev) => ({ ...prev, department: e.target.value }))}
            className="rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-hidden cursor-pointer"
          >
            <option value="ALL">All Departments</option>
            <option value="Information Technology">Information Technology</option>
            <option value="Engineering">Engineering</option>
            <option value="Operations">Operations</option>
            <option value="Human Resources">Human Resources</option>
          </select>
        </div>
      </div>

      {/* PR Enterprise Table with Lazy Loaded Display */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1 text-xs text-foreground-muted font-medium">
          <span>
            Showing <strong className="text-foreground">{displayedRecords.length}</strong> of{' '}
            <strong className="text-foreground">{records.length}</strong> Requisitions
          </span>
          {visibleRecordsCount < records.length && (
            <span className="text-[11px] text-primary animate-pulse">
              Scroll down for lazy loading...
            </span>
          )}
        </div>

        <PrTable
          records={displayedRecords}
          isLoading={isLoading}
          onCancelPr={(id) => setCancelPrId(id)}
          onRetrySapSync={handleRetrySap}
        />
      </div>

      {/* Cancellation Modal */}
      <PrConfirmationModal
        isOpen={!!cancelPrId}
        onClose={() => setCancelPrId(null)}
        onConfirm={handleConfirmCancel}
        title="Cancel Purchase Requisition"
        description="Are you sure you want to cancel this PR? Downstream SAP integration workflows will be terminated."
        confirmText="Confirm Cancellation"
        variant="danger"
        isSubmitting={isCancelling}
      />

      {/* Floating Back to Top Arrow */}
      {showBackToTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-40 h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-2xl flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-200 border-2 border-background cursor-pointer group"
          title="Back to Top"
        >
          <ArrowUp className="h-5 w-5 stroke-[2.5] group-hover:-translate-y-0.5 transition-transform" />
        </button>
      )}
    </div>
  );
};
